import React, { useState, useEffect } from 'react';
import { 
  Megaphone, Plus, Edit, Trash2, Pin, PinOff, 
  Paperclip, Download, FileText, Image as ImageIcon, 
  Video, File, ChevronDown, ChevronUp, Calendar, User
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import axios from 'axios';
import RichTextEditor from './RichTextEditor';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const getFileIcon = (tipo) => {
  switch (tipo) {
    case 'image': return <ImageIcon className="w-4 h-4 text-green-600" />;
    case 'video': return <Video className="w-4 h-4 text-purple-600" />;
    case 'document': return <FileText className="w-4 h-4 text-blue-600" />;
    default: return <File className="w-4 h-4 text-slate-600" />;
  }
};

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const AnnouncementCard = ({ announcement, canEdit, onEdit, onDelete, onTogglePin }) => {
  const [expanded, setExpanded] = useState(false);
  const contentPreview = announcement.contenido?.length > 300 && !expanded
    ? announcement.contenido.substring(0, 300) + '...'
    : announcement.contenido;

  return (
    <div className={`bg-white rounded-lg border shadow-sm overflow-hidden ${
      announcement.is_pinned ? 'border-amber-300 ring-1 ring-amber-200' : 'border-slate-200'
    }`}>
      {/* Header */}
      <div className={`px-4 py-3 flex items-start justify-between ${
        announcement.is_pinned ? 'bg-amber-50' : 'bg-slate-50'
      }`}>
        <div className="flex items-start gap-3">
          {announcement.is_pinned && (
            <Pin className="w-4 h-4 text-amber-600 mt-1 flex-shrink-0" />
          )}
          <div>
            <h3 className="font-semibold text-slate-900">{announcement.titulo}</h3>
            <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
              <span className="flex items-center gap-1">
                <User className="w-3 h-3" />
                {announcement.author_name}
              </span>
              <span className="flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {formatDate(announcement.created_at)}
              </span>
            </div>
          </div>
        </div>
        {canEdit && (
          <div className="flex items-center gap-1">
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-8 w-8 p-0"
              onClick={() => onTogglePin(announcement)}
              title={announcement.is_pinned ? "Desfijar" : "Fijar arriba"}
            >
              {announcement.is_pinned ? (
                <PinOff className="w-4 h-4 text-amber-600" />
              ) : (
                <Pin className="w-4 h-4 text-slate-400" />
              )}
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-8 w-8 p-0"
              onClick={() => onEdit(announcement)}
            >
              <Edit className="w-4 h-4 text-slate-600" />
            </Button>
            <Button 
              variant="ghost" 
              size="sm" 
              className="h-8 w-8 p-0"
              onClick={() => onDelete(announcement)}
            >
              <Trash2 className="w-4 h-4 text-red-500" />
            </Button>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="px-4 py-4">
        <div 
          className="prose prose-sm max-w-none text-slate-700"
          dangerouslySetInnerHTML={{ __html: contentPreview }}
        />
        {announcement.contenido?.length > 300 && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700 mt-2"
          >
            {expanded ? (
              <>Ver menos <ChevronUp className="w-4 h-4" /></>
            ) : (
              <>Ver más <ChevronDown className="w-4 h-4" /></>
            )}
          </button>
        )}
      </div>

      {/* Attachments */}
      {announcement.attachments && announcement.attachments.length > 0 && (
        <div className="px-4 pb-4 pt-2 border-t border-slate-100">
          <p className="text-xs font-medium text-slate-500 mb-2 flex items-center gap-1">
            <Paperclip className="w-3 h-3" />
            Archivos adjuntos ({announcement.attachments.length})
          </p>
          <div className="flex flex-wrap gap-2">
            {announcement.attachments.map((file, index) => (
              <a
                key={file.id || index}
                href={file.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-3 py-2 bg-slate-50 hover:bg-slate-100 rounded-md border border-slate-200 text-sm transition-colors"
              >
                {getFileIcon(file.tipo)}
                <span className="text-slate-700 max-w-[150px] truncate">{file.nombre}</span>
                {file.tamano && (
                  <span className="text-xs text-slate-400">({formatFileSize(file.tamano)})</span>
                )}
                <Download className="w-3 h-3 text-slate-400" />
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const AnnouncementBoard = ({ courseId, canPost = false }) => {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [form, setForm] = useState({
    titulo: '',
    contenido: '',
    is_pinned: false,
    attachments: []
  });
  const [uploading, setUploading] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    const loadAnnouncements = async () => {
      try {
        const response = await axios.get(`${API_URL}/online-courses/${courseId}/announcements`, {
          headers: getAuthHeaders()
        });
        setAnnouncements(response.data);
      } catch (error) {
        console.error('Error fetching announcements:', error);
        toast.error('Error al cargar los anuncios');
      } finally {
        setLoading(false);
      }
    };
    loadAnnouncements();
  }, [courseId]);

  const fetchAnnouncements = async () => {
    try {
      const response = await axios.get(`${API_URL}/online-courses/${courseId}/announcements`, {
        headers: getAuthHeaders()
      });
      setAnnouncements(response.data);
    } catch (error) {
      console.error('Error fetching announcements:', error);
      toast.error('Error al cargar los anuncios');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      titulo: '',
      contenido: '',
      is_pinned: false,
      attachments: []
    });
    setEditingAnnouncement(null);
  };

  const handleOpenDialog = (announcement = null) => {
    if (announcement) {
      setEditingAnnouncement(announcement);
      setForm({
        titulo: announcement.titulo,
        contenido: announcement.contenido,
        is_pinned: announcement.is_pinned || false,
        attachments: announcement.attachments || []
      });
    } else {
      resetForm();
    }
    setShowDialog(true);
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    resetForm();
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    
    for (const file of files) {
      // Convert to base64 for simplicity (in production, use proper file storage)
      const reader = new FileReader();
      reader.onload = (event) => {
        const tipo = file.type.startsWith('image/') ? 'image' 
          : file.type.startsWith('video/') ? 'video'
          : file.type.includes('pdf') || file.type.includes('document') ? 'document'
          : 'other';
        
        const newAttachment = {
          id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
          nombre: file.name,
          url: event.target.result,
          tipo: tipo,
          tamano: file.size
        };
        
        setForm(prev => ({
          ...prev,
          attachments: [...prev.attachments, newAttachment]
        }));
      };
      reader.readAsDataURL(file);
    }
    
    setUploading(false);
    e.target.value = ''; // Reset input
  };

  const removeAttachment = (attachmentId) => {
    setForm(prev => ({
      ...prev,
      attachments: prev.attachments.filter(a => a.id !== attachmentId)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.titulo.trim()) {
      toast.error('El título es obligatorio');
      return;
    }
    
    if (!form.contenido.trim()) {
      toast.error('El contenido es obligatorio');
      return;
    }

    try {
      const headers = { ...getAuthHeaders(), 'Content-Type': 'application/json' };
      if (editingAnnouncement) {
        await axios.put(`${API_URL}/online-courses/${courseId}/announcements/${editingAnnouncement.id}`, form, { headers });
        toast.success('Anuncio actualizado');
      } else {
        await axios.post(`${API_URL}/online-courses/${courseId}/announcements`, form, { headers });
        toast.success('Anuncio publicado');
      }
      handleCloseDialog();
      fetchAnnouncements();
    } catch (error) {
      console.error('Error saving announcement:', error);
      toast.error('Error al guardar el anuncio');
    }
  };

  const handleDelete = async (announcement) => {
    if (!window.confirm('¿Estás seguro de que quieres eliminar este anuncio?')) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/online-courses/${courseId}/announcements/${announcement.id}`, {
        headers: getAuthHeaders()
      });
      toast.success('Anuncio eliminado');
      fetchAnnouncements();
    } catch (error) {
      console.error('Error deleting announcement:', error);
      toast.error('Error al eliminar el anuncio');
    }
  };

  const handleTogglePin = async (announcement) => {
    try {
      await axios.patch(`${API_URL}/online-courses/${courseId}/announcements/${announcement.id}/pin`, {}, {
        headers: getAuthHeaders()
      });
      toast.success(announcement.is_pinned ? 'Anuncio desfijado' : 'Anuncio fijado');
      fetchAnnouncements();
    } catch (error) {
      console.error('Error toggling pin:', error);
      toast.error('Error al cambiar estado de fijado');
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-slate-200 rounded w-1/3"></div>
          <div className="h-24 bg-slate-100 rounded"></div>
          <div className="h-24 bg-slate-100 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-amber-50 to-orange-50">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-100 rounded-lg">
            <Megaphone className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <h2 className="font-semibold text-lg text-slate-900">Tablón de Anuncios</h2>
            <p className="text-sm text-slate-500">{announcements.length} {announcements.length === 1 ? 'anuncio' : 'anuncios'}</p>
          </div>
        </div>
        {canPost && (
          <Dialog open={showDialog} onOpenChange={setShowDialog}>
            <DialogTrigger asChild>
              <Button 
                onClick={() => handleOpenDialog()} 
                className="bg-amber-600 hover:bg-amber-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Anuncio
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>
                  {editingAnnouncement ? 'Editar Anuncio' : 'Nuevo Anuncio'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="titulo">Título *</Label>
                  <Input
                    id="titulo"
                    value={form.titulo}
                    onChange={(e) => setForm({ ...form, titulo: e.target.value })}
                    placeholder="Título del anuncio"
                    required
                  />
                </div>

                <div>
                  <Label>Contenido *</Label>
                  <RichTextEditor
                    content={form.contenido}
                    onChange={(content) => setForm({ ...form, contenido: content })}
                    placeholder="Escribe el contenido del anuncio..."
                  />
                </div>

                <div>
                  <Label>Archivos Adjuntos</Label>
                  <div className="mt-2">
                    <input
                      type="file"
                      id="file-upload"
                      multiple
                      onChange={handleFileUpload}
                      className="hidden"
                      accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                    />
                    <label
                      htmlFor="file-upload"
                      className="inline-flex items-center gap-2 px-4 py-2 border border-dashed border-slate-300 rounded-lg cursor-pointer hover:bg-slate-50 transition-colors"
                    >
                      <Paperclip className="w-4 h-4 text-slate-500" />
                      <span className="text-sm text-slate-600">
                        {uploading ? 'Subiendo...' : 'Adjuntar archivos'}
                      </span>
                    </label>
                  </div>
                  
                  {form.attachments.length > 0 && (
                    <div className="mt-3 space-y-2">
                      {form.attachments.map((file) => (
                        <div
                          key={file.id}
                          className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-md border"
                        >
                          <div className="flex items-center gap-2">
                            {getFileIcon(file.tipo)}
                            <span className="text-sm text-slate-700 truncate max-w-[200px]">
                              {file.nombre}
                            </span>
                            {file.tamano && (
                              <span className="text-xs text-slate-400">
                                ({formatFileSize(file.tamano)})
                              </span>
                            )}
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => removeAttachment(file.id)}
                          >
                            <Trash2 className="w-3 h-3 text-red-500" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="is_pinned"
                    checked={form.is_pinned}
                    onCheckedChange={(checked) => setForm({ ...form, is_pinned: checked })}
                  />
                  <Label htmlFor="is_pinned" className="flex items-center gap-2 cursor-pointer">
                    <Pin className="w-4 h-4 text-amber-600" />
                    Fijar anuncio arriba
                  </Label>
                </div>

                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button type="button" variant="outline" onClick={handleCloseDialog}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="bg-amber-600 hover:bg-amber-700">
                    {editingAnnouncement ? 'Guardar Cambios' : 'Publicar Anuncio'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Announcements List */}
      <div className="p-4 space-y-4">
        {announcements.length === 0 ? (
          <div className="text-center py-12">
            <Megaphone className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No hay anuncios publicados</p>
            {canPost && (
              <p className="text-sm text-slate-400 mt-1">
                Publica el primer anuncio para tus alumnos
              </p>
            )}
          </div>
        ) : (
          announcements.map((announcement) => (
            <AnnouncementCard
              key={announcement.id}
              announcement={announcement}
              canEdit={canPost}
              onEdit={handleOpenDialog}
              onDelete={handleDelete}
              onTogglePin={handleTogglePin}
            />
          ))
        )}
      </div>
    </div>
  );
};

export default AnnouncementBoard;
